import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { DateToShortReadableDate } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import { SimplifiedDocumentDataPropTypes } from '../../../propTypes/documents';
import { TableFilters } from '../../filters/tableFilters';
import { NoDocuments } from '../noDocuments/noDocuments';
import { FilterDocuments, SortDocumentsByDate } from '../../../helpers/docHelpers';
import { GetPetIconCssClassFromPolicyNumber } from '../../../helpers/petHelpers';
import { PetDownloadButton } from '../petDownloadButton/petDownloadButton';

export const PetDocumentsTable = ({ tableId, docType, petDetails, documentsData }) => {
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [isDescendingSort, setIsDescendingSort] = useState(true);
  const [documentsToShow, setDocumentsToShow] = useState(3);

  const handleSortOrderChange = () => {
    const newSortOrder = !isDescendingSort;
    setIsDescendingSort(newSortOrder);
    setFilteredDocs(SortDocumentsByDate({ documents: filteredDocs, descending: newSortOrder }));
  };

  const handleFilterChange = useCallback(
    ({ filterState }) => {
      if (filterState?.toDate && filterState?.fromDate) {
        const filteredDocuments = FilterDocuments({
          documents: documentsData,
          filterSettings: filterState
        });

        setFilteredDocs(SortDocumentsByDate({ documents: filteredDocuments, descending: isDescendingSort }));
      }
      setActiveFilters(filterState);
    },
    [isDescendingSort, documentsData]
  );

  const handleShowMore = () => {
    setDocumentsToShow((prevCount) => prevCount + 10);
  };

  return (
    <>
      <section className="petDocumentsPanel petDocumentsPanel_table">
        <TableFilters
          petDetails={petDetails}
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
          tableId={tableId}
        />

        <div className="petDocumentControls">
          <button
            type="button"
            onClick={handleSortOrderChange}
            className="date button text_only"
          >
            <Icon icon={isDescendingSort ? 'caret-up-solid' : 'caret-down-solid'} />
            <span className="labelText">Date</span>
          </button>

          <span className="pet">Pet</span>
          <span className="description">Description</span>
        </div>

        <div className="petDocuments">
          {filteredDocs.length === 0 && (
            <NoDocuments fullMessage={`You have no ${docType} documents for this pet/s and date range.`} />
          )}

          {filteredDocs?.slice(0, documentsToShow).map((document) => {
            const { attachmentNumber, attachmentId, animalName, petName, sentDate, policyNumber } = document;

            const readableSentDate = DateToShortReadableDate(new Date(sentDate));
            const petIconCssClass = GetPetIconCssClassFromPolicyNumber({
              petDetails,
              policyNumber
            });

            const fileName = `${petName || animalName}_${Math.floor(new Date(sentDate).getTime() / 1000)}`;

            return (
              <div
                key={attachmentNumber}
                className="petDocumentListItem"
              >
                <span className="date">{readableSentDate}</span>
                <span className="pet">
                  <Icon icon={petIconCssClass} />
                  {animalName || petName}
                </span>
                <span className="description">
                  <span>
                    <Icon icon="document-generic" />
                  </span>
                  {attachmentId ? (
                    <PetDownloadButton
                      policyNumber={policyNumber}
                      attachmentId={attachmentId}
                      fileName={fileName}
                      docType={docType}
                    />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </section>
      <div className="showMoreWrapper">
        {filteredDocs.length > 0 && filteredDocs.length > documentsToShow && (
          <button
            type="button"
            className="button secondary"
            onClick={handleShowMore}
          >
            Show more
          </button>
        )}
      </div>
    </>
  );
};

PetDocumentsTable.propTypes = {
  tableId: PropTypes.string,
  docType: PropTypes.string,
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  documentsData: PropTypes.arrayOf(SimplifiedDocumentDataPropTypes)
};
